/**
 * Splits a leading emoji off admin-authored copy ("⚡ Double payout week"
 * → ["⚡", "Double payout week"]).
 *
 * Why it exists: an emoji can't share a line with FK Screamer on iOS. The
 * emoji run raises the line's ascent past the box React Native measured
 * for the custom font, and every cap on that line loses its top ~2pt, at
 * any lineHeight. Measured on the sim: "DOUBLE PAYOUT" rendered 51px tall
 * with the ⚡ in the same Text and 56px (the full glyph, same as every
 * other Heading) once the emoji moved to a sibling Text. Callers render
 * the emoji beside the Heading, never inside it.
 *
 * Explicit code-point ranges rather than `\p{Extended_Pictographic}` so the
 * regex behaves the same on Hermes, JSC, Node and the web. Covers the
 * Arrows, Miscellaneous Technical, Miscellaneous Symbols / Dingbats and
 * Miscellaneous Symbols and Arrows blocks (⚡ ☀ ✅ ⏰ ⭐ ↔) and the
 * Supplemental Symbols and Pictographs planes (🏈 🎁 🔥).
 *
 * The whole grapheme is consumed, not just the first code point: a skin
 * tone (💪🏽), a variation selector (⚡️), a ZWJ sequence (👨‍👩‍👧), a tag
 * sequence (🏴󠁧󠁢󠁥󠁮󠁧󠁿) or a flag (🇺🇸, two regional indicators). Splitting one
 * of those mid-sequence would put half the emoji in the sibling Text and
 * leave the other half in the Heading, which both corrupts the glyph and
 * brings the clip back. So if the match stops in front of a continuation
 * character the regex doesn't know, nothing is split: the copy renders
 * as authored (clip and all) rather than as two broken halves.
 */
const PICTOGRAPH =
  '[\\u{2190}-\\u{21FF}\\u{2300}-\\u{23FF}\\u{2600}-\\u{27BF}\\u{2B00}-\\u{2BFF}\\u{1F000}-\\u{1FAFF}]';
// VS15/VS16, the five Fitzpatrick skin tones and tag characters, in any
// order, after a pictograph.
const MODIFIERS = '(?:[\\u{FE0E}\\u{FE0F}]|[\\u{1F3FB}-\\u{1F3FF}]|[\\u{E0020}-\\u{E007F}])*';
const FLAG = '[\\u{1F1E6}-\\u{1F1FF}]{2}';
const SEQUENCE = `${PICTOGRAPH}${MODIFIERS}(?:\\u{200D}${PICTOGRAPH}${MODIFIERS})*`;
const LEADING_EMOJI = new RegExp(`^\\s*(${FLAG}|${SEQUENCE})\\s*`, 'u');
// A code point that continues the emoji the regex just stopped in front of.
const CONTINUATION = /^(?:\u{200D}|[\u{FE0E}\u{FE0F}]|[\u{1F3FB}-\u{1F3FF}]|[\u{E0020}-\u{E007F}])/u;

export function splitLeadingEmoji(copy: string): [emoji: string | null, rest: string] {
  const match = LEADING_EMOJI.exec(copy);
  const emoji = match?.[1];
  if (!match || !emoji) return [null, copy];
  const rest = copy.slice(match[0].length);
  // Never leave the words empty (the Heading would be a blank header and
  // the emoji is hidden from VoiceOver), and never cut a sequence in half.
  if (rest === '' || CONTINUATION.test(rest)) return [null, copy];
  return [emoji, rest];
}
